import { useMemo } from "react";
import { MeetingType } from "../../api/graphql";
import useMayScheduleWebinars from "../../lib/useMayScheduleWebinars";
import { useCurrentVisitor } from "../../SessionBoundary";

export type WorkflowAction = "create" | "edit" | "duplicate";

type Props = {
  selectedMeetingType: MeetingType;
  stepsToRemove?: Step[];
};

export type Step =
  | "selectEventType"
  | "selectWho"
  | "addGuests"
  | "addItems"
  | "selectWhen"
  | "confirm"
  | "finished";

const defaultSteps: Step[] = ["selectWhen", "confirm", "finished"];

export default function useWorkflowSteps({
  selectedMeetingType,
  stepsToRemove,
}: Props): Step[] {
  const user = useCurrentVisitor();
  const mayScheduleWebinars = useMayScheduleWebinars();
  const orgMembership = user.organizationMemberships[0];

  // useMemo so the result can be a dependency for a useCallback
  return useMemo(() => {
    const isOrgMember = !!orgMembership;
    // Guests can be added by a user not in an organization or by an org member
    // with colleagues (skip addGuests step if user is the only member of their org)
    const canAddGuests =
      !isOrgMember || orgMembership.organization.memberships.length > 1;
    // Meeting items can be added by an org member scheduling in-person visit
    const canAddMeetingItems =
      isOrgMember && selectedMeetingType === MeetingType.InPersonVisit;

    const isWebinar = selectedMeetingType === MeetingType.Webinar;
    const steps: Step[] =
      mayScheduleWebinars && isWebinar
        ? // webinar scheduling steps
          ["selectEventType", "selectWho", ...defaultSteps]
        : // non-webinar scheduling steps
          [
            "selectWho",
            "selectEventType",
            ...(canAddGuests ? (["addGuests"] as Step[]) : []),
            ...(canAddMeetingItems ? (["addItems"] as Step[]) : []),
            ...defaultSteps,
          ];

    // If steps to be removed are provided, filter them out of the returned list
    if (stepsToRemove?.length)
      return steps.filter((step) => !stepsToRemove.includes(step));
    return steps;
  }, [mayScheduleWebinars, selectedMeetingType, orgMembership, stepsToRemove]);
}
